// src/components/ExaminationHistory.jsx
import React, { useState, useEffect } from "react";
import "./ExaminationHistory.css";
import axiosInstance from "../../Config/axiosConfig";
import {
  LoadingOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  HistoryOutlined as HistoryIcon,
  ArrowRightOutlined,
  PlayCircleOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import HistoryItem from "../HistoryItem/HistoryItem";

const ExaminationHistory = ({ 
  title = "Examination History",
  navigation,
  onViewAllPress,
  containerStyle = {},
  titleStyle = {},
  showViewAll = true,
  emptyMessage = "No examination history yet",
  limit = 3,
}) => {
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ completed: 0, averageScore: 0 });
  const [userId, setUserId] = useState('')

  const navigate = useNavigate('');

  // Fetch assessment results from API
  const fetchAssessmentResults = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Get required data from localStorage
      const accessToken = localStorage.getItem("accessToken");
      const storedDeviceId = localStorage.getItem('deviceId');
      const storedUserIp = localStorage.getItem('userIp');

      // Get user ID from localStorage - directly use the variable, don't rely on state
      let userIdFromStorage = '';
      const userInfoString = localStorage.getItem("userInfo");
      
      if (userInfoString) {
        try {
          const userInfo = JSON.parse(userInfoString);
          userIdFromStorage = userInfo.userId; // Store in local variable
          setUserId(userIdFromStorage); // Still update state for other uses
          console.log("User ID from storage:", userIdFromStorage);
        } catch (parseError) {
          console.error("Error parsing userInfo:", parseError);
          setError("Invalid user information");
          setLoading(false);
          return;
        }
      } else {
        setError("Please login to view assessment history");
        setLoading(false);
        return;
      }

      if (!accessToken) {
        setError("Authentication required. Please login again.");
        setLoading(false);
        return;
      }

      // Make API request using the LOCAL variable, not the state
      const response = await axiosInstance.get(
        `Assessment/get-user-assessment-results/${userIdFromStorage}`, // Use the local variable here
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            UserDevice: storedDeviceId,
            UserIPAddress: storedUserIp,
          },
        }
      );

      console.log('API Response:', JSON.stringify(response.data, null, 2));

      if (response.data && response.data.code === 200) {
        // Transform API data to match our component structure
        const transformedData = transformApiData(response.data.data);
        setData(transformedData);
        
        // Calculate stats
        calculateStats(transformedData);
      } else {
        setError(response.data?.message || 'Failed to fetch assessment results');
      }

    } catch (error) {
      console.error('Error fetching assessment results:', error);
      setError(error.message || 'An error occurred while fetching data');
      setData([]); // Clear any previous data on error
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Transform API response to match our component data structure
  const transformApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    return apiData.map((item, index) => ({
      id: item.assessmentId?.toString() || index.toString(),
      title: item.name || item.examName || 'Untitled Assessment',
      type: getAssessmentType(item.assessmentTypeName),
      date: item.completedDate || item.createdDate || new Date().toISOString().split('T')[0],
      score: item.total_score,
      classLevel: item.class_level_name,
      totalQuestions: item.total_questions || item.questionCount || 0,
      timeSpent: formatTimeSpent(item.time_used_seconds),
      status: getStatus(item),
      icon: getIconBySubject(item.subject || item.category),
      color: getColorBySubject(item.subject || item.category),
      rawData: item,
    }));
  };

  // Helper functions for data transformation
  const getAssessmentType = (type) => {
    if (!type) return 'Assessment';
    
    const typeLower = type.toLowerCase();
    if (typeLower.includes('exam')) return 'Exam';
    if (typeLower.includes('quiz')) return 'Quiz';
    if (typeLower.includes('test')) return 'Test';
    if (typeLower.includes('practice')) return 'Practice';
    return 'Assessment';
  };

  const formatTimeSpent = (seconds) => {
    if (!seconds || seconds === 0) return '0 secs';
  
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    const parts = [];
    
    if (hours > 0) {
      parts.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    }
    
    if (minutes > 0) {
      parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    }
    
    if (remainingSeconds > 0 || parts.length === 0) {
      parts.push(`${remainingSeconds} sec${remainingSeconds !== 1 ? 's' : ''}`);
    }
    
    return parts.join(' ');
  };

  const getStatus = (item) => {
    if (item.status === 'completed' || item.isCompleted) {
      return 'completed';
    }
    if (item.status === 'in-progress' || item.isInProgress) {
      return 'in-progress';
    }
    return 'completed';
  };

  const getIconBySubject = (subject) => {
    const iconMap = {
      'mathematics': 'calculator',
      'math': 'calculator',
      'physics': 'bulb',
      'chemistry': 'fire',
      'biology': 'leaf',
      'english': 'book',
      'history': 'history',
      'geography': 'globe',
      'science': 'experiment',
      'computer': 'laptop',
      'default': 'file-text',
    };
    
    const subjectLower = subject?.toLowerCase() || '';
    for (const [key, icon] of Object.entries(iconMap)) {
      if (subjectLower.includes(key)) {
        return icon;
      }
    }
    return iconMap.default;
  };

  const getColorBySubject = (subject) => {
    const colorMap = {
      'mathematics': '#29d159ff',
      'math': '#29d159ff',
      'physics': '#4a90e2',
      'chemistry': '#ff6b6b',
      'biology': '#4CAF50',
      'english': '#9C27B0',
      'history': '#FF9800',
      'geography': '#795548',
      'science': '#2196F3',
      'computer': '#607D8B',
      'default': '#666666',
    };
    
    const subjectLower = subject?.toLowerCase() || '';
    for (const [key, color] of Object.entries(colorMap)) {
      if (subjectLower.includes(key)) {
        return color;
      }
    }
    return colorMap.default;
  };

  const calculateStats = (assessmentData) => {
    const completed = assessmentData.filter(item => item.status === 'completed').length;
    const completedWithScores = assessmentData.filter(
      item => item.status === 'completed' && item.score > 0
    );
    
    const averageScore = completedWithScores.length > 0
      ? Math.round(
          completedWithScores.reduce((acc, item) => acc + item.score, 0) / completedWithScores.length
        )
      : 0;
    
    setStats({ completed, averageScore });
  };

  const handleItemPress = (item) => {
    if (item.status === 'completed') {
      // Navigate to exam results page
      navigate(`/exam-result/${item.rawData.uid}`, { 
        state: { examData: item.rawData } 
      });
    } else if (item.status === 'in-progress') {
      // Continue exam or show resume option
      console.log('Resume exam:', item.rawData.uid);
      // You can navigate to the exam continue page if you have one
      // navigate(`/exam-continue/${item.rawData.uid}`);
    }
    console.log('Pressed item:', item.rawData.uid);
  };

  const handleViewAllPress = () => {
    if (onViewAllPress) {
      onViewAllPress(data);
    } else {
      navigate('/history-exam', { 
        state: { assessmentData: data } 
      });
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchAssessmentResults();
  };

  useEffect(() => {
    fetchAssessmentResults();
  }, []);

  // Show loading indicator
  if (loading && !refreshing) {
    return (
      <div className="examination-history-container" style={containerStyle}>
        <div className="examination-history-header">
          <h3 className="examination-history-title" style={titleStyle}>
            {title}
          </h3>
          <LoadingOutlined spin />
        </div>
        <div className="examination-loading-container">
          <LoadingOutlined spin style={{ fontSize: 48, color: '#4a90e2' }} />
          <p className="examination-loading-text">Loading assessment history...</p>
        </div>
      </div>
    );
  }

  // Show error message
  if (error) {
    return (
      <div className="examination-history-container" style={containerStyle}>
        <div className="examination-history-header">
          <h3 className="examination-history-title" style={titleStyle}>
            {title}
          </h3>
        </div>
        <div className="examination-error-container">
          <ExclamationCircleOutlined className="examination-error-icon" />
          <p className="examination-error-text">{error}</p>
          <button 
            className="examination-retry-button"
            onClick={fetchAssessmentResults}
          >
            <ReloadOutlined /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="examination-history-container" style={containerStyle}>
      {/* Header */}
      <div className="examination-history-header">
        <div>
          <h3 className="examination-history-title" style={titleStyle}>
            {title}
          </h3>
          {data.length > 0 && (
            <p className="examination-history-subtitle">
              {stats.completed} completed • Avg: {stats.averageScore}%
            </p>
          )}
        </div>
        {showViewAll && data.length > 0 && (
          <button 
            className="examination-view-all-button"
            onClick={handleViewAllPress}
          >
            View All <ArrowRightOutlined />
          </button>
        )}
      </div>

      {/* History List */}
      {data.length > 0 ? (
        <div className="examination-list-container">
          <div className="examination-list">
            {data.slice(0, limit).map((item, index) => (
              <React.Fragment key={item.id}>
                <HistoryItem
                  item={item}
                  index={index}
                  onClick={handleItemPress}
                  // You can pass additional props if HistoryItem needs them
                />
                {index < Math.min(data.length - 1, limit - 1) && (
                  <div className="examination-item-separator"></div>
                )}
              </React.Fragment>
            ))}
          </div>
          {refreshing && (
            <div className="examination-refresh-indicator">
              <LoadingOutlined spin />
              <span>Refreshing...</span>
            </div>
          )}
        </div>
      ) : (
        <div className="examination-empty-container">
          <HistoryIcon className="examination-empty-icon" />
          <p className="examination-empty-text">{emptyMessage}</p>
          <button 
            className="examination-start-button"
            onClick={() => navigate('/exams')}
          >
            <PlayCircleOutlined /> Start Your First Exam
          </button>
        </div>
      )}
    </div>
  );
};

export default ExaminationHistory;